import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import TrackerDetail from './TrackerDetail';
import { fetchInfluencer, saveTracker, getRapidApiKey } from '../../utils/instagramTrackerApi';

// Merge campaign posts into trackedAds — campaign posts are read-only (is_campaign_link: true)
function mergeCampaignPosts(existingAds, campaignPosts) {
  if (!campaignPosts || campaignPosts.length === 0) return existingAds;

  const result = [...existingAds];
  for (const cp of campaignPosts) {
    if (!cp.url) continue;
    // Extract shortcode from URL to use as id
    const parts = cp.url.split('/').filter(p => p !== '');
    const pidx = parts.indexOf('p') > -1 ? parts.indexOf('p') : parts.indexOf('reel');
    const shortcode = pidx > -1 && parts[pidx + 1] ? parts[pidx + 1].split('?')[0] : cp.url;

    // Skip if already tracked (by id or URL match)
    if (result.some(a => a.id === shortcode || (a.url && a.url.includes(shortcode)))) continue;

    result.push({
      id: shortcode,
      url: cp.url,
      reelViews: cp.views || 0,
      likes: cp.likes || 0,
      comments: cp.comments || 0,
      format: 'Reel',
      caption: 'Campaign Deliverable',
      is_campaign_link: true,
      campaign_name: cp.campaign_name || 'Campaign',
      tracked_at: cp.synced_at || new Date().toISOString(),
    });
  }
  return result;
}

export default function TrackerModal({ instagramHandle, initialData, ratePerReel, campaignPosts, onClose, onSyncComplete, onTrackerUpdated }) {
  const [loading, setLoading] = useState(!initialData);
  const [trackerData, setTrackerData] = useState(() => {
    if (!initialData) return null;
    // On first load, merge campaign posts into existing trackedAds
    const merged = mergeCampaignPosts(initialData.trackedAds || [], campaignPosts || []);
    return { ...initialData, trackedAds: merged };
  });

  useEffect(() => {
    if (instagramHandle && !initialData) {
      loadInfluencer();
    }
  }, [instagramHandle]);

  // When campaignPosts arrive (async), merge them into current trackerData
  useEffect(() => {
    if (!trackerData || !campaignPosts || campaignPosts.length === 0) return;
    const merged = mergeCampaignPosts(trackerData.trackedAds || [], campaignPosts);
    if (merged.length !== (trackerData.trackedAds || []).length) {
      const updated = { ...trackerData, trackedAds: merged };
      setTrackerData(updated);
      // Persist to localStorage so they survive a refresh
      const un = instagramHandle.replace('@', '').trim().toLowerCase();
      saveTracker(un, updated).catch(console.error);
      if (onTrackerUpdated) onTrackerUpdated(un, updated);
    }
  }, [campaignPosts]);

  const loadInfluencer = async () => {
    try {
      setLoading(true);
      const un = instagramHandle.replace('@', '').trim().toLowerCase();
      const key = await getRapidApiKey();
      if (!key) {
        toast.error('Please configure your RapidAPI Key first (Settings gear at top of page)');
        onClose();
        return;
      }
      await handleFetchFresh(un);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load tracker');
      onClose();
    }
  };

  const handleFetchFresh = async (username) => {
    setLoading(true);
    try {
      const un = (username || instagramHandle).replace('@', '').trim().toLowerCase();
      toast.success(`Analyzing @${un}... taking approx 15s`);

      const prevHistory = trackerData?.history || [];
      const prevTrackedAds = trackerData?.trackedAds || [];

      const data = await fetchInfluencer(un);

      const newSnapshot = {
        date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }),
        timestamp: new Date().toISOString(),
        followers: data.profile.followers || 0
      };

      data.history = [...prevHistory, newSnapshot];
      // Preserve manually tracked ads + merge campaign posts
      data.trackedAds = mergeCampaignPosts(prevTrackedAds, campaignPosts || []);

      await saveTracker(un, data);
      setTrackerData(data);
      if (onTrackerUpdated) onTrackerUpdated(un, data);
      if (onSyncComplete && data.computed) {
        onSyncComplete({
          followers: data.profile.followers,
          engagement_rate: parseFloat(data.computed.engagementRate),
          profilePicUrl: data.profile.profilePicUrl || ''
        });
      }
      toast.success(`Successfully analyzed @${un}`);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8">
      <div className="bg-salty-card w-full max-w-7xl h-full max-h-[90vh] rounded-2xl border border-salty-border shadow-2xl flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-salty-text-main relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg bg-salty-bg hover:bg-salty-border text-salty-text-muted hover:text-salty-text-main transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <RefreshCw className="w-10 h-10 animate-spin text-salty-purple mb-4" />
            <h3 className="text-xl font-bold">Analyzing Live Instagram Data</h3>
            <p className="text-salty-text-muted mt-2">Fetching 12 most recent posts, generating charts, calculating viral ratios...</p>
          </div>
        ) : trackerData ? (
          <div className="overflow-y-auto p-6 scrollbar-hide h-full">
            <TrackerDetail
              influencer={trackerData}
              ratePerReel={ratePerReel || 0}
              onClose={onClose}
              onRefresh={() => handleFetchFresh(instagramHandle)}
              onUpdateAds={async (newAds) => {
                const updated = { ...trackerData, trackedAds: newAds };
                setTrackerData(updated);
                const un = instagramHandle.replace('@', '').trim().toLowerCase();
                await saveTracker(un, updated);
                if (onTrackerUpdated) onTrackerUpdated(un, updated);
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-salty-text-main">
            <p>Could not load influencer data.</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-salty-border rounded text-salty-text-main">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
